import React, { useContext, useState } from "react";

import { Alert, Stack, Typography } from "@mui/material";

import { deleteOrgRestrictionOverride } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { toRestrictionName } from "constants/restrictions";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface RemoveOrgRestrictionOverrideProps {
  open: boolean;
  organizationId: string;
  regionCode: string;
  restrictionName: string;
  onClose: () => void;
  onSuccess: () => void;
}

// RemoveOrgRestrictionOverride returns one restriction to the org's plan
// default, the counterpart to EditOrgRestriction's write. It is gated on
// REMOVABLE_ORG_RESTRICTION_NAMES, not the edit list — see the comment there.
//
// It cannot be offered only where an override exists: GetOrgRestrictions
// returns the resolved value with the override already layered in, and nothing
// in the response says which layer produced it. The dialog therefore says so
// rather than implying a state Periscope cannot read.
const RemoveOrgRestrictionOverride = ({
  open,
  organizationId,
  regionCode,
  restrictionName,
  onClose,
  onSuccess,
}: RemoveOrgRestrictionOverrideProps) => {
  const { showError } = useContext(SnackbarContext);
  const [isRemoving, setIsRemoving] = useState(false);

  // DeleteOrgRestrictionOverride takes the numeric enum, while the restrictions
  // map is keyed by the full enum name.
  const restrictionNameValue = toRestrictionName(restrictionName);

  const handleConfirm = async () => {
    if (restrictionNameValue === undefined) return;

    setIsRemoving(true);
    try {
      await deleteOrgRestrictionOverride(
        { organizationId, restrictionName: restrictionNameValue },
        callOptionsHeaders(regionCode, organizationId),
      );
      onSuccess();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <ConfirmDialog
      title={`Remove ${restrictionName.replace("RESTRICTION_NAME_", "")} override`}
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) onClose();
      }}
      onConfirm={handleConfirm}
      onCancel={onClose}
      cancelBtnLabel="Cancel"
      confirmBtnLabel="Remove"
      disabled={isRemoving || restrictionNameValue === undefined}
      disableCloseOnConfirm
      disableBackgroundClick={isRemoving}
    >
      <Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
        <Alert severity="warning">
          This drops the per-org override and returns the org to whatever its
          plan grants. The effective limit can increase or decrease.
        </Alert>
        <Alert severity="info" data-testid="remove-override-unknown-state">
          Periscope cannot tell whether this org has an override — the API
          returns only the resolved value. If there is none, this changes
          nothing.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Organisation: {organizationId}
        </Typography>
      </Stack>
    </ConfirmDialog>
  );
};

export default RemoveOrgRestrictionOverride;
